/**
 * The console's theme, as values.
 *
 * Pure on purpose: no storage, no DOM, no React. `src/lib/theme-store.ts`
 * persists the choice and `src/components/theme-provider.tsx` paints it, so
 * every rule about what a preference MEANS can be read and tested without
 * either of them.
 *
 * Why a class and not `prefers-color-scheme`: `src/index.css` repoints
 * Tailwind's `dark:` variant at `.dark`, so a reader can hold a theme the OS
 * does not. The media query is still the DEFAULT, which is what `system` is.
 */

/** What a reader can hold. `system` defers to the OS setting. */
export type ThemePreference = 'light' | 'dark' | 'system'

/** What actually gets painted, with `system` already resolved away. */
export type Theme = 'light' | 'dark'

/**
 * The preference a reader who has never chosen a theme holds.
 *
 * `system`, not `light`: a staff laptop set to dark should open dark, and the
 * console has no standing to overrule the OS until somebody says otherwise.
 */
export const DEFAULT_PREFERENCE: ThemePreference = 'system'

/** The class `ThemeProvider` puts on the document element for a dark paint. */
export const DARK_CLASS = 'dark'

/** The media query carrying the OS setting. */
export const DARK_QUERY = '(prefers-color-scheme: dark)'

/**
 * Narrow an untrusted value to a preference.
 *
 * Anything can be typed into Web Storage by hand, so the value is checked
 * rather than cast. Three literal comparisons rather than a list membership
 * test, because these narrow the type without an assertion — and
 * `typescript/no-unsafe-type-assertion` is an error in `src/`.
 *
 * @param raw - Whatever came out of storage.
 * @returns The preference, or null when it is not one.
 */
export function parsePreference(raw: unknown): ThemePreference | null {
  if (raw === 'light' || raw === 'dark' || raw === 'system') {
    return raw
  }

  return null
}

/**
 * Narrow an untrusted value to a painted theme.
 *
 * A display theme is only `dark` or `light`. `system` is a reader preference,
 * not a theme a TV paints, so it is rejected here.
 *
 * @param raw - Whatever a document or a response carried.
 * @returns The theme, or null when it is not one.
 */
export function parseTheme(raw: unknown): Theme | null {
  if (raw === 'light' || raw === 'dark') {
    return raw
  }

  return null
}

/**
 * Resolve a held preference against the OS setting.
 *
 * @param preference - What the reader holds.
 * @param systemPrefersDark - Whether the OS asks for dark.
 * @returns The theme to paint.
 */
export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean
): Theme {
  if (preference === 'system') {
    return systemPrefersDark ? 'dark' : 'light'
  }

  return preference
}

/**
 * The preference one press of the toggle produces.
 *
 * A press flips what is on screen, so it is a two-state control and never a
 * three-state cycle. The toggle never produces `system`: the account menu's
 * System choice does. A reader who has stated a preference keeps it across
 * an OS change, and one who holds `system` keeps following the OS.
 *
 * @param current - The theme on screen right now.
 * @returns The preference to hold next.
 */
export function nextPreference(current: Theme): ThemePreference {
  return current === 'dark' ? 'light' : 'dark'
}
